/**
 * PropertiesPanel — Right-side panel for editing zone properties.
 *
 * Listens to Fabric.js selection events and renders zone-specific
 * edit controls (position, text style, colours, camera angles, etc.).
 * Changes update the zone definition in the current template and
 * trigger a re-render via the provided callback.
 *
 * @module PropertiesPanel
 */

/**
 * @typedef {Object} PropertiesPanelOptions
 * @property {(zoneId: string, updates: object) => void} [onZoneUpdate]
 *   Called when a zone's properties are changed. The host should apply
 *   the updates to the template zone and trigger a re-render.
 * @property {() => void} [onDeselect]
 *   Called when the user deselects / closes the panel.
 */

export class PropertiesPanel {
  /** @type {HTMLElement} */
  #container;

  /** @type {HTMLElement} */
  #body;

  /** @type {((zoneId: string, updates: object) => void) | null} */
  #onZoneUpdate;

  /** @type {(() => void) | null} */
  #onDeselect;

  /** @type {object|null} */
  #currentZone = null;

  /**
   * @param {HTMLElement} containerElement The #props-panel-body element
   * @param {PropertiesPanelOptions} [options]
   */
  constructor(containerElement, options = {}) {
    this.#container = containerElement;
    this.#onZoneUpdate = options.onZoneUpdate ?? null;
    this.#onDeselect = options.onDeselect ?? null;
    this.#showEmptyState();
  }

  /**
   * Show the properties for a zone.
   * @param {object} zone Zone definition from template
   */
  showZone(zone) {
    this.#currentZone = zone;
    this.#container.innerHTML = '';
    this.#container.classList.remove('empty-state');

    const header = this.#makeHeader(zone);
    this.#container.appendChild(header);

    // Common: position & size
    this.#container.appendChild(this.#makeSection('Position & Size', [
      this.#makeNumberRow('X', zone.bounds.x, (v) => this.#update({ 'bounds.x': v })),
      this.#makeNumberRow('Y', zone.bounds.y, (v) => this.#update({ 'bounds.y': v })),
      this.#makeNumberRow('Width', zone.bounds.width, (v) => this.#update({ 'bounds.width': v })),
      this.#makeNumberRow('Height', zone.bounds.height, (v) => this.#update({ 'bounds.height': v })),
    ]));

    // Zone-specific controls
    switch (zone.type) {
      case 'text':
        this.#container.appendChild(this.#makeTextControls(zone));
        break;
      case 'magazineCover':
        this.#container.appendChild(this.#makeCoverControls(zone));
        break;
      case 'magazineSpread':
        this.#container.appendChild(this.#makeSpreadControls(zone));
        break;
      case 'badge':
      case 'cta':
        this.#container.appendChild(this.#makeBadgeControls(zone));
        break;
    }
  }

  /**
   * Clear the panel and show empty state.
   */
  clear() {
    this.#currentZone = null;
    this.#showEmptyState();
  }

  // ── Private: UI builders ──────────────────────────────────────────

  #showEmptyState() {
    this.#container.innerHTML = `
      <div class="empty-state">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" opacity="0.2"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
        <p>Select a zone on the canvas to edit its properties</p>
      </div>
    `;
  }

  /**
   * @param {object} zone
   * @returns {HTMLElement}
   */
  #makeHeader(zone) {
    const div = document.createElement('div');
    div.className = 'props-header';
    div.innerHTML = `
      <span class="props-zone-type">${zone.type}</span>
      <span class="props-zone-label">${zone.label || zone.id || ''}</span>
      <button class="props-close-btn" title="Deselect">&times;</button>
    `;
    div.querySelector('.props-close-btn')?.addEventListener('click', () => {
      this.clear();
      this.#onDeselect?.();
    });
    return div;
  }

  /**
   * @param {string} title
   * @param {HTMLElement[]} rows
   * @returns {HTMLElement}
   */
  #makeSection(title, rows) {
    const section = document.createElement('div');
    section.className = 'props-section';

    const h = document.createElement('button');
    h.className = 'props-section-title';
    h.type = 'button';
    h.innerHTML = `<span class="props-section-caret">▾</span><span>${title}</span>`;

    const body = document.createElement('div');
    body.className = 'props-section-body';
    for (const row of rows) body.appendChild(row);

    h.addEventListener('click', () => {
      section.classList.toggle('collapsed');
    });

    section.appendChild(h);
    section.appendChild(body);
    return section;
  }

  /**
   * @param {string} label
   * @param {number} value
   * @param {(v: number) => void} onChange
   * @returns {HTMLElement}
   */
  #makeNumberRow(label, value, onChange) {
    const row = document.createElement('div');
    row.className = 'props-row';
    row.innerHTML = `
      <label class="props-label">${label}</label>
      <input type="number" class="props-input props-input-num" value="${Math.round(value)}">
    `;
    const input = row.querySelector('input');
    input.addEventListener('input', () => {
      const v = parseFloat(input.value);
      if (!isNaN(v)) onChange(v);
    });
    return row;
  }

  /**
   * @param {string} label
   * @param {string} value
   * @param {(v: string) => void} onChange
   * @returns {HTMLElement}
   */
  #makeTextRow(label, value, onChange) {
    const row = document.createElement('div');
    row.className = 'props-row';
    row.innerHTML = `
      <label class="props-label">${label}</label>
      <input type="text" class="props-input" value="${value || ''}">
    `;
    const input = row.querySelector('input');
    input.addEventListener('input', () => onChange(input.value));
    return row;
  }

  /**
   * @param {string} label
   * @param {string} value
   * @param {(v: string) => void} onChange
   * @returns {HTMLElement}
   */
  #makeColourRow(label, value, onChange) {
    const row = document.createElement('div');
    row.className = 'props-row';
    row.innerHTML = `
      <label class="props-label">${label}</label>
      <div class="props-colour-wrap">
        <input type="color" class="props-colour-picker" value="${this.#normaliseColour(value)}">
        <input type="text" class="props-input props-colour-text" value="${value || ''}">
      </div>
    `;
    const picker = row.querySelector('.props-colour-picker');
    const text = row.querySelector('.props-colour-text');
    picker.addEventListener('input', () => {
      text.value = picker.value;
      onChange(picker.value);
    });
    text.addEventListener('input', () => {
      const c = this.#normaliseColour(text.value);
      if (c) picker.value = c;
      onChange(text.value);
    });
    return row;
  }

  /**
   * @param {string} label
   * @param {number} value
   * @param {number} min
   * @param {number} max
   * @param {number} step
   * @param {(v: number) => void} onChange
   * @param {object} [opts]
   * @param {string} [opts.unit] Unit suffix shown after the value (e.g. '°')
   * @param {string} [opts.tooltip] Hover hint describing the control
   * @returns {HTMLElement}
   */
  #makeSliderRow(label, value, min, max, step, onChange, opts = {}) {
    const unit = opts.unit ?? '';
    const row = document.createElement('div');
    row.className = 'props-row props-row--slider';
    if (opts.tooltip) row.title = opts.tooltip;
    row.innerHTML = `
      <label class="props-label">${label}<span class="props-slider-value">${value}${unit}</span></label>
      <div class="props-slider-wrap">
        <input type="range" class="props-slider" min="${min}" max="${max}" step="${step}" value="${value}">
        <button class="props-reset-btn" title="Reset to ${value}${unit}" tabindex="-1">⟳</button>
      </div>
    `;
    const slider = row.querySelector('input');
    const valSpan = row.querySelector('.props-slider-value');
    const resetBtn = row.querySelector('.props-reset-btn');
    const initial = value;
    slider.addEventListener('input', () => {
      const v = parseFloat(slider.value);
      valSpan.textContent = `${v}${unit}`;
      onChange(v);
    });
    resetBtn.addEventListener('click', () => {
      slider.value = String(initial);
      valSpan.textContent = `${initial}${unit}`;
      onChange(initial);
    });
    return row;
  }

  /**
   * @param {string} label
   * @param {string} value
   * @param {string[]} options
   * @param {(v: string) => void} onChange
   * @returns {HTMLElement}
   */
  #makeSelectRow(label, value, options, onChange) {
    const row = document.createElement('div');
    row.className = 'props-row';
    row.innerHTML = `
      <label class="props-label">${label}</label>
      <select class="props-input">
        ${options.map(o => `<option value="${o}" ${o === value ? 'selected' : ''}>${o}</option>`).join('')}
      </select>
    `;
    const select = row.querySelector('select');
    select.addEventListener('change', () => onChange(select.value));
    return row;
  }

  // ── Zone-specific control sections ────────────────────────────────

  /**
   * @param {object} zone
   * @returns {HTMLElement}
   */
  #makeTextControls(zone) {
    const s = zone.style || {};
    return this.#makeSection('Text Style', [
      this.#makeTextRow('Font Family', s.fontFamily, (v) => this.#updateStyle({ fontFamily: v })),
      this.#makeNumberRow('Font Size', s.fontSize || 24, (v) => this.#updateStyle({ fontSize: v })),
      this.#makeSelectRow('Font Weight', String(s.fontWeight || 400), ['200', '300', '400', '600', '700', '800', '900'], (v) => this.#updateStyle({ fontWeight: parseInt(v) })),
      this.#makeColourRow('Fill Colour', s.fill, (v) => this.#updateStyle({ fill: v })),
      this.#makeSelectRow('Text Align', s.textAlign || 'left', ['left', 'center', 'right', 'justify'], (v) => this.#updateStyle({ textAlign: v })),
    ]);
  }

  /**
   * @param {object} zone
   * @returns {HTMLElement}
   */
  #makeCoverControls(zone) {
    return this.#makeMagazineControls(zone, 'cover');
  }

  /**
   * @param {object} zone
   * @returns {DocumentFragment}
   */
  #makeSpreadControls(zone) {
    return this.#makeMagazineControls(zone, 'spread');
  }

  /**
   * Shared control builder for magazine cover/spread zones. Produces three
   * sections: Orientation, Image (paper/zoom/pan) and Appearance (shadow/finish).
   * @param {object} zone
   * @param {'cover'|'spread'} kind
   * @returns {DocumentFragment}
   */
  #makeMagazineControls(zone, kind) {
    const t = zone.transform || {};
    const frag = document.createDocumentFragment();

    // — Interaction hint —
    const hint = document.createElement('div');
    hint.className = 'props-hint';
    hint.innerHTML = 'Click+drag to <b>move</b> · shift+drag or double-click to <b>orbit</b> · scroll to <b>zoom</b>';
    frag.appendChild(hint);

    // — Presets —
    frag.appendChild(this.#makePresetRow(kind));

    // — Orientation —
    const orient = [
      this.#makeSliderRow('Tilt', t.cameraTiltX ?? (kind === 'cover' ? 35 : 45), 0, 90, 1,
        (v) => this.#updateTransform({ cameraTiltX: v }),
        { unit: '°', tooltip: 'Look-down angle. 0° = top-down (flat lay), 90° = side-on.' }),
      this.#makeSliderRow('Turn', t.cameraRotateY ?? (kind === 'cover' ? -20 : 0), -180, 180, 1,
        (v) => this.#updateTransform({ cameraRotateY: v }),
        { unit: '°', tooltip: 'Orbit around the magazine — turn to see it from front, side, or back.' }),
      this.#makeSliderRow('Spin', t.cameraRotateZ ?? 0, -180, 180, 1,
        (v) => this.#updateTransform({ cameraRotateZ: v }),
        { unit: '°', tooltip: 'Rotate the magazine flat on the table.' }),
    ];
    frag.appendChild(this.#makeSection('Orientation', orient));

    // — Image framing —
    const imageRows = [
      this.#makeMappedSelectRow('Paper', t.aspectRatio ?? null, [
        { label: 'From Image', value: null },
        { label: 'A4 Portrait', value: 1.414 },
        { label: 'US Letter', value: 1.294 },
        { label: 'Square', value: 1 },
        { label: 'Landscape 4:3', value: 0.75 },
      ], (v) => this.#updateTransform({ aspectRatio: v })),
    ];
    // Curl only applies to spreads (covers use a rigid box).
    if (kind === 'spread') {
      imageRows.push(this.#makeSliderRow('Curl', t.curveIntensity ?? 0.12, 0, 0.5, 0.01,
        (v) => this.#updateTransform({ curveIntensity: v }),
        { tooltip: 'Lift the outer page edge above the table (fanned look). Spine height & curve shape are set by Binding + Pages.' }));
    }
    imageRows.push(
      this.#makeSliderRow('Zoom', t.zoom ?? 1, 1, 3, 0.05,
        (v) => this.#updateTransform({ zoom: v }),
        { unit: '×', tooltip: 'Zoom into the image (crops the edges).' }),
      this.#makeSliderRow('Pan X', t.panX ?? 0, -1, 1, 0.05,
        (v) => this.#updateTransform({ panX: v }),
        { tooltip: 'Move the image left/right within the page.' }),
      this.#makeSliderRow('Pan Y', t.panY ?? 0, -1, 1, 0.05,
        (v) => this.#updateTransform({ panY: v }),
        { tooltip: 'Move the image up/down within the page.' }),
    );
    frag.appendChild(this.#makeSection('Image', imageRows));

    // — Appearance —
    frag.appendChild(this.#makeSection('Appearance', [
      this.#makeSliderRow('Shadow', t.shadowStrength ?? 0.25, 0, 0.6, 0.01,
        (v) => this.#updateTransform({ shadowStrength: v }),
        { tooltip: 'Strength of the ground contact shadow.' }),
      this.#makeSliderRow('Finish', t.roughness ?? 0.3, 0, 1, 0.05,
        (v) => this.#updateTransform({ roughness: v }),
        { tooltip: '0 = glossy, 1 = matte paper finish.' }),
    ]));

    return frag;
  }

  /**
   * Build a row of one-click orientation presets for a magazine zone.
   * @param {'cover'|'spread'} kind
   * @returns {HTMLElement}
   */
  #makePresetRow(kind) {
    const presets = kind === 'cover'
      ? [
          { label: 'Flat Lay', t: { cameraTiltX: 8, cameraRotateY: 0, cameraRotateZ: 0 } },
          { label: 'Hero', t: { cameraTiltX: 45, cameraRotateY: -25, cameraRotateZ: 5 } },
          { label: 'Front', t: { cameraTiltX: 22, cameraRotateY: 0, cameraRotateZ: 0 } },
          { label: 'Standing', t: { cameraTiltX: 80, cameraRotateY: -15, cameraRotateZ: 0 } },
        ]
      : [
          { label: 'Flat Lay', t: { cameraTiltX: 10, cameraRotateY: 0, cameraRotateZ: 0 } },
          { label: 'Hero', t: { cameraTiltX: 50, cameraRotateY: 0, cameraRotateZ: 10 } },
          { label: 'Fanned', t: { cameraTiltX: 55, cameraRotateY: 0, cameraRotateZ: 15 } },
          { label: 'Open Book', t: { cameraTiltX: 35, cameraRotateY: 0, cameraRotateZ: 0 } },
        ];

    const section = document.createElement('div');
    section.className = 'props-section';
    const wrap = document.createElement('div');
    wrap.className = 'props-presets';
    for (const p of presets) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'props-preset-btn';
      btn.textContent = p.label;
      btn.addEventListener('click', () => this.#applyPreset(p.t));
      wrap.appendChild(btn);
    }
    section.appendChild(wrap);
    return section;
  }

  /**
   * Apply a preset patch of transform values, then refresh the panel controls.
   * @param {Record<string, number>} patch
   */
  #applyPreset(patch) {
    if (!this.#currentZone) return;
    if (!this.#currentZone.transform) this.#currentZone.transform = {};
    Object.assign(this.#currentZone.transform, patch);
    const updates = {};
    for (const key of Object.keys(patch)) updates[`transform.${key}`] = patch[key];
    this.#update(updates);
    // Rebuild controls so sliders reflect the new values.
    this.showZone(this.#currentZone);
  }

  /**
   * Select row that maps option labels to arbitrary values (incl. null/number).
   * @param {string} label
   * @param {*} currentValue
   * @param {{label: string, value: *}[]} pairs
   * @param {(v: *) => void} onChange
   * @returns {HTMLElement}
   */
  #makeMappedSelectRow(label, currentValue, pairs, onChange) {
    const row = document.createElement('div');
    row.className = 'props-row';

    const lbl = document.createElement('label');
    lbl.className = 'props-label';
    lbl.textContent = label;

    const sel = document.createElement('select');
    sel.className = 'props-input';
    pairs.forEach((p, i) => {
      const opt = document.createElement('option');
      opt.value = String(i);
      opt.textContent = p.label;
      if (p.value === currentValue) opt.selected = true;
      sel.appendChild(opt);
    });
    sel.addEventListener('change', () => onChange(pairs[parseInt(sel.value)].value));

    row.appendChild(lbl);
    row.appendChild(sel);
    return row;
  }

  /**
   * @param {object} zone
   * @returns {HTMLElement}
   */
  #makeBadgeControls(zone) {
    const s = zone.style || {};
    return this.#makeSection('Badge / CTA Style', [
      this.#makeColourRow('BG Colour', s.backgroundColor, (v) => this.#updateStyle({ backgroundColor: v })),
      this.#makeColourRow('Text Colour', s.textColor, (v) => this.#updateStyle({ textColor: v })),
      this.#makeNumberRow('Border Radius', s.borderRadius || 0, (v) => this.#updateStyle({ borderRadius: v })),
      this.#makeNumberRow('Font Size', s.fontSize || 24, (v) => this.#updateStyle({ fontSize: v })),
      this.#makeNumberRow('Font Weight', s.fontWeight || 700, (v) => this.#updateStyle({ fontWeight: v })),
    ]);
  }

  // ── Private: Update helpers ───────────────────────────────────────

  /**
   * Push updates to the host. Uses dot-notation keys for nested props.
   * @param {Record<string, *>} updates
   */
  #update(updates) {
    if (!this.#currentZone || !this.#onZoneUpdate) return;
    this.#onZoneUpdate(this.#currentZone.id, updates);
  }

  /**
   * Update a style property.
   * @param {Record<string, *>} styleUpdates
   */
  #updateStyle(styleUpdates) {
    if (!this.#currentZone) return;
    if (!this.#currentZone.style) this.#currentZone.style = {};
    Object.assign(this.#currentZone.style, styleUpdates);
    // Build dot-notation keys for the host
    const updates = {};
    for (const key of Object.keys(styleUpdates)) {
      updates[`style.${key}`] = styleUpdates[key];
    }
    this.#update(updates);
  }

  /**
   * Update a transform property.
   * @param {Record<string, *>} transformUpdates
   */
  #updateTransform(transformUpdates) {
    if (!this.#currentZone) return;
    if (!this.#currentZone.transform) this.#currentZone.transform = {};
    Object.assign(this.#currentZone.transform, transformUpdates);
    // Build dot-notation keys for the host
    const updates = {};
    for (const key of Object.keys(transformUpdates)) {
      updates[`transform.${key}`] = transformUpdates[key];
    }
    this.#update(updates);
  }

  /**
   * Convert any colour string to a #rrggbb hex for the colour picker.
   * @param {string} c
   * @returns {string}
   */
  #normaliseColour(c) {
    if (!c) return '#000000';
    if (c.startsWith('#') && c.length === 7) return c;
    // Try canvas approach
    const ctx = document.createElement('canvas').getContext('2d');
    ctx.fillStyle = c;
    const computed = ctx.fillStyle;
    if (computed.startsWith('#')) return computed;
    return '#000000';
  }
}
